import React, { useState } from 'react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../lib/ui/select'
import { Button } from '../../../../lib/ui/button'
import useTranslation, {
  TranslationProvider,
} from '../../../shared/TranslationProvider'
import translations from '../../locales.json'
import AylaResponseScrollArea from '../../AylaResponseScrollArea'
import SavedStatusIcon from '~/components/shared/SavedStatusIcon'
import AylaLoading from '~/components/shared/AylaLoading'
import AylaTimeoutError, { aylaPost } from '~/components/shared/AylaTimeout'

const LocationPriorities = ({ locale, ...props }) => {
  return (
    <TranslationProvider
      locale={locale}
      translations={translations}
      scope={
        'javascript.components.ShortCourses.work-environment.location-preferences'
      }
    >
      <LocationPrioritiesInner {...props} />
    </TranslationProvider>
  )
}
export default LocationPriorities
const LocationPrioritiesInner = ({
  documentId,
  selectedAttributes,
  attributes,
}) => {
  const { t } = useTranslation()
  const questions = t('questions')
  const [locationType, setLocationType] = useState()
  const [locationRestrictons, setLocationRestrictions] = useState('')
  const [typingComplete, setTypingComplete] = useState(false)
  const [displayedText, setDisplayedText] = useState('')
  const [index, setIndex] = useState(0)
  const [aylaResponse, setAylaResponse] = useState(null)
  const [submitted, setSubmitted] = useState(false)
  const showCursor = submitted && displayedText.length <= 0
  const showBorder = submitted || aylaResponse
  const [loadingStatus, setLoadingStatus] = useState(null)

  const getAylaPrompt = (attr) => {
    if (!attributes) return null
    return attributes.find((a) => a.text === attr.criteria)?.ayla_prompt
  }

  const getLocationPreferences = () => {
    const list = []
    selectedAttributes.forEach((attr) => {
      const aylaPrompt = getAylaPrompt(attr)
      if (aylaPrompt) {
        list.push(aylaPrompt)
      }
    })
    return list.join(', ')
  }

  const locationPreferences = getLocationPreferences()
  const urbanSuburban = locationType
    ? ` In addition, ensure these target locations are ${locationType}.`
    : ''
  const userRestriction =
    locationRestrictons.length > 0
      ? ` I have a restriction on my target locations which is: ${locationRestrictons}. `
      : ' '

  const prompt = t('ayla_prompt', {
    location_preferences: locationPreferences,
    urban_suburban: urbanSuburban,
    user_restriction: userRestriction,
  })

  const requestAylaStatement = async () => {
    const url = `/job-search/create_ai_response?document_id=${documentId}`
    const data = { ayla_prompt: { prompt } }
    try {
      const res = await aylaPost(url, data)
      setAylaResponse(res.data.ayla_response)
      setSubmitted(false)
      setLoadingStatus('complete')
    } catch {
      setLoadingStatus('failed')
    }
  }

  const handleButtonClick = () => {
    setAylaResponse(null)
    setDisplayedText('')
    setIndex(0)
    setTypingComplete(false)
    setSubmitted(true)
    requestAylaStatement()
    setLoadingStatus('loading')
  }

  return (
    <div>
      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-2">
          <div className="subtitle">{t('ayla_title')}</div>
          <div>
            <ul className="list-disc pl-4">
              {selectedAttributes.map((attribute, idx) => (
                <li key={`selected-location-attributes-${idx}`}>
                  {attribute.criteria}
                </li>
              ))}
            </ul>
          </div>
          <p>{t('ayla_description')}</p>
        </div>
        <p>{t('ayla_activity')}</p>
        <div>
          {questions.map((questionInfo, idx) => (
            <div key={`location-priorities-ayla-${idx}`}>
              <label htmlFor={questionInfo.slug}>{questionInfo.question}</label>
              {questionInfo.type === 'text' && (
                <textarea
                  className="min-h-[125px] w-full rounded-[20px] border border-[#D3D3D3] bg-[#F7FAFC] p-3 font-roboto text-base"
                  onChange={(e) => setLocationRestrictions(e.target.value)}
                  value={locationRestrictons || ''}
                  id={questionInfo.slug}
                />
              )}
              {questionInfo.type !== 'text' && (
                <Select
                  onValueChange={(e) => setLocationType(e)}
                  value={locationType || ''}
                  className="w-full rounded-[20px] border border-[#D3D3D3] bg-[#F7FAFC] p-3 font-roboto text-base"
                  id={questionInfo.slug}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('select_an_option')} />
                  </SelectTrigger>
                  <SelectContent>
                    {questionInfo.options.map((option, index) => (
                      <SelectItem key={index} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          ))}
        </div>
        <Button
          disabled={submitted}
          className="self-start"
          onClick={handleButtonClick}
        >
          {t('cta')}
          <SavedStatusIcon loadingStatus={loadingStatus} />
        </Button>
      </div>
      {loadingStatus === 'loading' && (
        <div className="flex items-center justify-center">
          <AylaLoading />
        </div>
      )}
      {loadingStatus === 'failed' && (
        <AylaTimeoutError onRetry={handleButtonClick} />
      )}
      {loadingStatus === 'complete' && (
        <AylaResponseScrollArea
          showBorder={showBorder}
          showCursor={showCursor}
          aylaResponse={aylaResponse}
          typingComplete={typingComplete}
          setTypingComplete={setTypingComplete}
          displayedText={displayedText}
          setDisplayedText={setDisplayedText}
          index={index}
          setIndex={setIndex}
        />
      )}
    </div>
  )
}
